import { decode } from "@msgpack/msgpack";
var zip = require("lodash.zip");

// Utility functions
function uint8ToUint16(uint8) {
  return new Int16Array(new Uint8Array(uint8).buffer);
}

function uint8ToUint32(uint8) {
  return new Int32Array(new Uint8Array(uint8).buffer);
}

function normalizeEnvelopeFormat(rawObj) {
  if (!rawObj || typeof rawObj !== "object") return rawObj;
  if (!rawObj.H || !Array.isArray(rawObj.D)) return rawObj;

  const header = rawObj.H || {};
  const firstData = (rawObj.D && rawObj.D[0]) || {};
  const normalizedType = rawObj.type ?? header.FT;

  return {
    ...rawObj,
    ...firstData,
    productID: rawObj.productID ?? header.PrjID,
    type: typeof normalizedType === "string" ? normalizedType.toUpperCase() : normalizedType,
    version: rawObj.version ?? header.v,
    t: rawObj.t ?? header.t
  };
}

function removeOffset(wave) {
  return wave;

  //const avg = wave.reduce((a, b) => a + b, 0) / wave.length;
  //buaca el maxim y el minimo de la onda
  const max = Math.max(...wave);
  const min = Math.min(...wave);
  //saca el promedio
  const avg = (max + min) / 2;
  return wave.map((x) => x - avg);
}

function getPropertyByPath(obj, path) {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

function setPropertyByPath(obj, path, value) {
  const parts = path.split('.');
  const last = parts.pop();
  const target = parts.reduce((acc, part) => acc && acc[part], obj);
  if (target) {
    target[last] = value;
  }
}

const MEASURE_CALIBRATION_MAP = [
  // Formato nuevo (COEFF en mayúsculas, MEASURE en minúsculas)
  { measureKey: "fcr", coeffKey: "FCR" },
  { measureKey: "fcs", coeffKey: "FCS" },
  { measureKey: "fct", coeffKey: "FCT" },
  { measureKey: "pir", coeffKey: "PIR" },
  { measureKey: "pis", coeffKey: "PIS" },
  { measureKey: "pit", coeffKey: "PIT" },
  { measureKey: "pin", coeffKey: "PIN" },
  { measureKey: "zfr", coeffKey: "ZFR" },
  { measureKey: "zfs", coeffKey: "ZFS" },
  { measureKey: "zft", coeffKey: "ZFT" },
];

// Redondea a 6 decimales si es un número
function roundTo6(value) {
  if (typeof value !== 'number' || !isFinite(value)) return value;
  return Math.round(value * 1e6) / 1e6;
}

function applyMeasureCalibration(measure, coeff) {
  console.log("calibrando", measure,coeff);
  
  if (!measure || typeof measure !== "object" || !coeff || typeof coeff !== "object") {
    return measure;
  }

  const calibrated = { ...measure };

  MEASURE_CALIBRATION_MAP.forEach(({ measureKey, coeffKey }) => {
    const factor = coeff[coeffKey];
    if (factor === undefined || calibrated[measureKey] === undefined) {
      return;
    }

    const value = calibrated[measureKey];

    if (Array.isArray(value)) {
      calibrated[measureKey] = value.map((v) => roundTo6(v * factor));
    } else if (ArrayBuffer.isView(value) && typeof value.map === "function") {
      calibrated[measureKey] = value.map((v) => roundTo6(v * factor));
    } else if (typeof value === "number") {
      calibrated[measureKey] = roundTo6(value * factor);
    }
  });

  return calibrated;
}

const SPECIAL_PARSERS = {
  timestamp: (value) => new Date(value * 1000).toLocaleString(),
  version: (value) => {
      let v = value;
      if (typeof v !== 'bigint') {
          try {
              v = BigInt(value);
          } catch (e) {
              return value;
          }
      }
      const major = Number((v >> 48n) & 0xFFFFn);
      const minor = Number((v >> 32n) & 0xFFFFn);
      const patch = Number((v >> 16n) & 0xFFFFn);
      const revision = Number(v & 0xFFFFn);
      return `${major}.${minor}.${patch}.${revision}`;
  },
  calibrateMeasure: (value, root) => applyMeasureCalibration(value, root?.COEFF)
};

// Configuration objects for different document types
const PRODUCT_CONFIGS = {
  "24P002": {
    types: {
      0: {
        versions: {
          0: {
            fields: ["VA", "VB", "VC", "IA", "IB", "IC", "IN"],
            dataProcessor: uint8ToUint32,
            applyOffset: false,
            excludedFields: ["COEFF.FCR"],
            gridConfig: {
              voltageFields: ["VA", "VB", "VC"],
              currentFields: ["IA", "IB", "IC", "IN"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            }
          },
          // Versiones 1, 2, 3 comparten la misma configuración
          "1,2,3": {
            fields: ["FCR", "FCS", "FCT", "PIR", "PIS", "PIT", "PIN", "ZFR", "ZFS", "ZFT"],
            dataProcessor: uint8ToUint32,
            applyOffset: false,
            enableDownload: true,
            gridConfig: {
              voltageFields: ["FCR", "FCS", "FCT"],
              currentFields: ["PIR", "PIS", "PIT", "PIN", "ZFR", "ZFS", "ZFT"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            }
          },
          // O puedes usar wildcard para versiones 4 en adelante
          "*": {
            fields: ["FCR", "FCS", "FCT", "PIR", "PIS", "PIT", "PIN", "ZFR", "ZFS", "ZFT"],
            dataProcessor: uint8ToUint32,
            applyOffset: false,
            enableDownload: true,
            specialParsers: {
              "t": "timestamp",
              "SYSSTATUS.VERSION": "version",
              "MEASURE": "calibrateMeasure"
            },
            gridConfig: {
              voltageFields: ["FCR", "FCS", "FCT"],
              currentFields: ["PIR", "PIS", "PIT", "PIN", "ZFR", "ZFS", "ZFT"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            }
          }
        }
      },
      1: {
        versions: {
          "*": {
            fields: ["FCR", "FCS", "FCT", "PIR", "PIS", "PIT", "PIN", "ZFR", "ZFS", "ZFT"],
            dataProcessor: uint8ToUint16,
            applyOffset: false,
            offsetFields: ["FCR", "FCS", "FCT"],
            specialParsers: {
              "t": "timestamp",
              "SYSSTATUS.VERSION": "version",
              "MEASURE": "calibrateMeasure"
            },
            gridConfig: {
              voltageFields: ["FCR", "FCS", "FCT"],
              currentFields: ["PIR", "PIS", "PIT", "PIN", "ZFR", "ZFS", "ZFT"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            }
          }
        }
      },
      3: {
        versions: {
          "*": {
            fields: ["FCR", "FCS", "FCT", "PIR", "PIS", "PIT", "PIN", "ZFR", "ZFS", "ZFT"],
            dataProcessor: uint8ToUint32,
            applyOffset: false,
            enableDownload: true,
            specialParsers: {
              "t": "timestamp",
              "SYSSTATUS.VERSION": "version",
              "MEASURE": "calibrateMeasure"
            },
            gridConfig: {
              voltageFields: ["FCR", "FCS", "FCT"],
              currentFields: ["PIR", "PIS", "PIT", "PIN", "ZFR", "ZFS", "ZFT"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            }
          }
        }
      },
      4: {
        versions: {
          "*": {
            fields: ["FCR", "FCS", "FCT", "PIR", "PIS", "PIT"],
            dataProcessor: uint8ToUint16,
            applyOffset: false,
            offsetFields: ["FCR", "FCS", "FCT"],
            excludedFields: [],
            specialParsers: {
              "t": "timestamp",
              "SYSSTATUS.VERSION": "version",
              "MEASURE": "calibrateMeasure"
            },
            gridConfig: {
              voltageFields: ["FCR", "FCS", "FCT"],
              currentFields: ["PIR", "PIS", "PIT"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            },
            scaleConfig: {
              voltage: { fields: ["FCR", "FCS", "FCT"], factor: 1, removeOffset: false },
              current: { fields: ["PIR", "PIS", "PIT"], factor: 0.037014, removeOffset: false }
            },
            titleTemplate: (data) => `24P002 MEASURE12`
          }
        }
      },
      5: {
        versions: {
          "*": {
            fields: ["FCR", "FCS", "FCT", "PIR", "PIS", "PIT"],
            dataProcessor: uint8ToUint16,
            applyOffset: false,
            offsetFields: ["FCR", "FCS", "FCT"],
            axisConfig: {
              timeAxis: true
            },
            specialParsers: {
              "t": "timestamp",
              "SYSSTATUS.VERSION": "version",
              "MEASURE": "calibrateMeasure"
            },
            gridConfig: {
              voltageFields: ["FCR", "FCS", "FCT"],
              currentFields: ["PIR", "PIS", "PIT"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            },
            scaleConfig: {
              voltage: { fields: ["FCR", "FCS", "FCT"], factor: 1, removeOffset: false },
              current: { fields: ["PIR", "PIS", "PIT"], factor: 0.037014, removeOffset: false }
            },
            titleTemplate: (data) => `24P002 Fault`
          }
        }
      },
    }
  },
  "22P003": {
    types: {
      0: {
        versions: {
          "*": {
            fields: ["VA", "VB", "VC", "IA", "IB", "IC"],
            dataProcessor: uint8ToUint32,
            applyOffset: false,
            gridConfig: {
              voltageFields: ["VA", "VB", "VC"],
              currentFields: ["IA", "IB", "IC"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            },
            titleTemplate: (data) => `22P003: ${data.ADE}`
          }
        }
      }
    }
  },
  "25P004": {
    types: {
      0: {
        versions: {
          "*": {
            fields: ["PIR", "PIS", "PIT", "PIN", "PICA1", "PICA2", "PICA3", "PICA4"],
            dataProcessor: uint8ToUint32,
            applyOffset: false,
            gridConfig: {
              voltageFields: ["PIR", "PIS", "PIT", "PIN"],
              currentFields: ["PICA1", "PICA2", "PICA3", "PICA4"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            },
            titleTemplate: (data) => `25P004 MEASURE24`
          }
        }
      },
      1: {
        versions: {
          "*": {
            fields: ["PIR", "PIS", "PIT", "PIN", "PICA1", "PICA2", "PICA3", "PICA4"],
            dataProcessor: uint8ToUint16,
            applyOffset: false,
            gridConfig: {
              voltageFields: ["PIR", "PIS", "PIT", "PIN"],
              currentFields: ["PICA1", "PICA2", "PICA3", "PICA4"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            },
            titleTemplate: (data) => `25P004 MEASURE16`
          }
        }
      },
      4: {
        versions: {
          "*": {
            fields: ["PICA1", "PICA2", "PICA3", "PIR", "PIS", "PIT"],
            dataProcessor: uint8ToUint16,
            applyOffset: false,
            gridConfig: {
              voltageFields: ["PIR", "PIS", "PIT"],
              currentFields: ["PICA1", "PICA2", "PICA3"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            },
            titleTemplate: (data) => `25P004 MEASURE12`
          }
        }
      },
      5: {
        versions: {
          "*": {
            fields: ["PICA1", "PICA2", "PICA3", "PIR", "PIS", "PIT"],
            dataProcessor: uint8ToUint16,
            applyOffset: false,
            axisConfig: {
              timeAxis: true
            },
            gridConfig: {
              voltageFields: ["PIR", "PIS", "PIT"],
              currentFields: ["PICA1", "PICA2", "PICA3"],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            },
            titleTemplate: (data) => `25P004 Fault`
          }
        }
      },
    }
  },
  "25P021": {
    types: {
      FAULT: {
        versions: {
          "*": {
            fields: ["FAULT_CURRENT_BUFFER"],
            dataProcessor: uint8ToUint16,
            applyOffset: false,
            specialParsers: {
              "t": "timestamp",
            },
            gridConfig: {
              voltageFields: ["FAULT_CURRENT_BUFFER"],
              currentFields: [],
              grids: [{ bottom: "60%" }, { top: "60%" }]
            },
            titleTemplate: () => "25P021 Fault"
          }
        }
      }
    }
  }
};

// Generic processing functions
function processDataFields(deserializedObj, config) {
  const data = {
    type: deserializedObj.type,
    productID: deserializedObj.productID
  };

  // Process main fields
  config.fields.forEach(field => {
    if (deserializedObj[field]) {
      data[field] = config.dataProcessor(deserializedObj[field]);
    }
  });

  // Apply offset removal if configured
  if (config.applyOffset && config.offsetFields) {
    config.offsetFields.forEach(field => {
      if (data[field]) {
        data[field] = removeOffset(data[field]);
      }
    });
  }

  // Handle custom mappings
  if (config.customMaps) {
    Object.entries(config.customMaps).forEach(([mapName, mapArray]) => {
      const mapKey = mapName.toLowerCase();
      if (deserializedObj[mapKey] !== undefined) {
        data[mapName] = mapArray[deserializedObj[mapKey]];
      }
    });
  }

  return data;
}

function createEChartsConfig(data, config, meta = {}) {
  const { fields, gridConfig } = config;
  const hasBottomPane = Array.isArray(gridConfig?.currentFields) && gridConfig.currentFields.length > 0;
  const timeAxisEnabled = Boolean(config.axisConfig?.timeAxis)
    && Number.isFinite(meta.timestampSec)
    && Number.isFinite(meta.sampleFrequency)
    && meta.sampleFrequency > 0;
  const formatTimeMs = (ms) => {
    const date = new Date(ms);
    const pad = (num, size) => String(num).padStart(size, "0");
    const hh = pad(date.getHours(), 2);
    const mm = pad(date.getMinutes(), 2);
    const ss = pad(date.getSeconds(), 2);
    const msPart = pad(date.getMilliseconds(), 3);
    return `${hh}:${mm}:${ss}.${msPart}`;
  };

  const timeAxisFormatter = timeAxisEnabled
    ? function (value, idx) {
        const baseMs = meta.timestampSec * 1000;
        const stepMs = 1000 / meta.sampleFrequency;
        const ms = baseMs + Number(value) * stepMs;
        return formatTimeMs(ms);
      }
    : undefined;
  
  // Create dataset
  const fieldArrays = fields.map(field => data[field]);
  const dataset = zip(...fieldArrays).map((row, index) => [index, ...row]);
  
  // Create series configuration
  const series = fields.map((name, i) => {
    const isVoltage = gridConfig.voltageFields.includes(name);
    const axisIndex = hasBottomPane ? (isVoltage ? 0 : 1) : 0;
    return {
      name: name,
      type: "line",
      large: true,
      sampling: "lttb",
      smooth: true,
      lineStyle: { width: 1 },
      areaStyle: null,
      encode: { x: "index", y: name },
      xAxisIndex: axisIndex,
      yAxisIndex: axisIndex,
    };
  });

  const xAxisBase = timeAxisEnabled
    ? { type: "category", axisLabel: { formatter: timeAxisFormatter } }
    : { type: "category" };

  const defaultSingleGrid = { top: "12%", bottom: "14%" };
  const grid = hasBottomPane
    ? gridConfig.grids
    : [config.singlePaneGrid || defaultSingleGrid];

  const xAxis = hasBottomPane
    ? [xAxisBase, { ...xAxisBase, gridIndex: 1 }]
    : [xAxisBase];

  const yAxis = hasBottomPane
    ? [{ type: "value" }, { type: "value", gridIndex: 1 }]
    : [{ type: "value" }];

  const chartConfig = {
    dataset: {
      source: dataset,
      dimensions: ["index", ...fields],
    },
    grid: grid,
    xAxis: xAxis,
    yAxis: yAxis,
    legend: {
      data: fields,
      itemGap: 5,
    },
    series: series,
  };

  if (timeAxisEnabled) {
    chartConfig.tooltip = {
      trigger: "axis",
      formatter: function (params) {
        const baseMs = meta.timestampSec * 1000;
        const stepMs = 1000 / meta.sampleFrequency;
        const axisValue = Array.isArray(params) && params.length ? params[0].axisValue : 0;
        const ms = baseMs + Number(axisValue) * stepMs;
        const header = formatTimeMs(ms);
        const lines = (Array.isArray(params) ? params : []).map((p) => {
          const marker = p.marker || "";
          return `${marker}${p.seriesName}: ${p.data?.[p.encode?.y?.[0]] ?? p.value?.[p.encode?.y?.[0]] ?? p.value}`;
        });
        return [header, ...lines].join("<br/>");
      }
    };
  }

  // Add title if configured
  if (config.titleTemplate && typeof config.titleTemplate === 'function') {
    chartConfig.title = { text: config.titleTemplate(data) };
  } else {
    chartConfig.title = { text: '' };
  }

  return chartConfig;
}

function getDocumentConfig(productID, type, version) {
  const product = PRODUCT_CONFIGS[productID];
  if (!product) return null;

  const typeConfig = product.types[type];
  if (!typeConfig) return null;

  // Check for specific version first
  let versionConfig = typeConfig.versions[version];
  
  // If not found, check for version ranges or arrays
  if (!versionConfig) {
    // Check for version arrays like "1,2,3"
    for (const [versionKey, config] of Object.entries(typeConfig.versions)) {
      if (versionKey.includes(',')) {
        const supportedVersions = versionKey.split(',').map(v => parseInt(v.trim()));
        if (supportedVersions.includes(version)) {
          versionConfig = config;
          break;
        }
      }
    }
  }
  
  // Fall back to wildcard
  if (!versionConfig) {
    versionConfig = typeConfig.versions["*"];
  }
  
  return versionConfig;
}

// Generic document processor
function processDocument(deserializedObj, config, filename, meta) {
  if (!config) {
    console.error("Invalid document configuration");
    return null;
  }

  console.log(deserializedObj);

  // Process data fields according to configuration
  const data = processDataFields(deserializedObj, config);

  // Create ECharts configuration
  return createEChartsConfig(data, config, meta);
}

// Main entry point - now uses configuration-driven approach
export function processFile(file, filename) {
  const deserializedObj = normalizeEnvelopeFormat(decode(file));

  const rawTimestampSec = deserializedObj?.t;
  const rawSampleFrequency = deserializedObj?.config?.sampleFrequency;

  if (!deserializedObj.productID) {
    console.log("No productID found:", deserializedObj);
    return null;
  }

  // Get configuration for this document
  const config = getDocumentConfig(
    deserializedObj.productID, 
    deserializedObj.type, 
    deserializedObj.version
  );

  if (!config) {
    console.error(`Unsupported document: productID=${deserializedObj.productID}, type=${deserializedObj.type}, version=${deserializedObj.version}`);
    console.log("Available configurations:", Object.keys(PRODUCT_CONFIGS));
    return null;
  }

  // Apply special parsers to deserializedObj
  if (config.specialParsers) {
      Object.entries(config.specialParsers).forEach(([path, parserName]) => {
        const value = getPropertyByPath(deserializedObj, path);
          if (value !== undefined) {
              const parser = SPECIAL_PARSERS[parserName];
                if (parser) {
                  const newValue = parser(value, deserializedObj);
                  setPropertyByPath(deserializedObj, path, newValue);
              }
          }
      });
  }

  // Process using the generic processor
  const option = processDocument(deserializedObj, config, filename, {
    timestampSec: Number(rawTimestampSec),
    sampleFrequency: Number(rawSampleFrequency)
  });
  return { option, deserializedObj, config };
}

// Helper function to add new product configurations
export function addProductConfig(productID, typeConfig) {
  if (!PRODUCT_CONFIGS[productID]) {
    PRODUCT_CONFIGS[productID] = { types: {} };
  }
  
  Object.assign(PRODUCT_CONFIGS[productID].types, typeConfig);
}

// Helper function to get all supported products and their types
export function getSupportedDocuments() {
  const supported = {};
  
  Object.entries(PRODUCT_CONFIGS).forEach(([productID, product]) => {
    supported[productID] = {};
    Object.entries(product.types).forEach(([type, typeConfig]) => {
      supported[productID][type] = Object.keys(typeConfig.versions);
    });
  });
  
  return supported;
}
